/**
 * Client Approval Portal — single inbox for reviewers/clients to approve or
 * request changes on scripts / edits / thumbnails / publish-ready videos.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, XCircle, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function StudioApprovals() {
  const [items, setItems] = useState(null);
  const [activeNote, setActiveNote] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/studio/approvals/pending`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch (_) { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (item, decision) => {
    setBusyId(item._id + decision);
    try {
      await axios.post(`${API}/api/studio/approvals`, {
        content_id: item._id,
        artefact: item.artefact,
        decision,
        note: activeNote[item._id] || null,
      }, { withCredentials: true });
      toast.success(decision === "approve" ? "Approved ✓" : "Changes requested");
      setActiveNote((x) => ({ ...x, [item._id]: "" }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  return (
    <>
      <Header />
      <div className="studio-shell">
        <Link to="/studio" className="studio-back" data-testid="appr-back">
          <ArrowLeft size={14}/> Back to Studio
        </Link>
        <div style={{ marginTop: 12 }}>
          <span className="studio-eyebrow"><CheckCircle2 size={12}/> Approvals inbox</span>
          <h2 style={{ margin: "6px 0 18px" }}>Pending your review</h2>
        </div>
        {items === null ? (
          <div className="studio-card studio-loading">
            <Loader2 size={22} className="ac-spin" style={{ color: "var(--st-purple, #a78bfa)" }}/>
          </div>
        ) : items.length === 0 ? (
          <div className="studio-card"><p className="studio-muted">All caught up — no approvals waiting on you.</p></div>
        ) : (
          <div style={{ display: "grid", gap: 14 }}>
            {items.map((it) => (
              <motion.div key={it._id}
                className="studio-card"
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                data-testid={`approval-${it._id}`}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div>
                    <h3 style={{ margin: "0 0 4px", color: "#0F172A" }}>{it.title}</h3>
                    <div style={{ display: "flex", gap: 8 }}>
                      <span className="studio-pill studio-pill--sm">
                        <FileText size={10}/> {it.artefact}
                      </span>
                      <span className="studio-pill studio-pill--sm">{it.stage}</span>
                    </div>
                  </div>
                  <Link
                    to={`/studio/channels/${it.channel_id}#content-${it._id}`}
                    className="studio-link"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    View →
                  </Link>
                </div>
                <textarea
                  className="studio-ai-script-text"
                  style={{ minHeight: 70, marginTop: 14 }}
                  placeholder="Optional note (visible to the team)…"
                  value={activeNote[it._id] || ""}
                  onChange={(e) => setActiveNote((x) => ({ ...x, [it._id]: e.target.value }))}
                  data-testid={`approval-note-${it._id}`}
                />
                <div className="studio-form-actions">
                  <button
                    className="studio-btn-ghost"
                    onClick={() => decide(it, "request_changes")}
                    disabled={busyId === it._id + "request_changes"}
                    data-testid={`approval-changes-${it._id}`}
                  >
                    {busyId === it._id + "request_changes"
                      ? <Loader2 size={14} className="ac-spin"/>
                      : <XCircle size={14}/>} Request changes
                  </button>
                  <button
                    className="studio-btn-primary"
                    onClick={() => decide(it, "approve")}
                    disabled={busyId === it._id + "approve"}
                    data-testid={`approval-approve-${it._id}`}
                  >
                    {busyId === it._id + "approve"
                      ? <Loader2 size={14} className="ac-spin"/>
                      : <CheckCircle2 size={14}/>} Approve
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
